# Questionnaires

A job ad may specify a questionnaire to include in SEEK’s Apply Form.
Candidates complete the questionnaire as part of their job application,
and their responses are made available through the [Optimised Apply] use case.
Questionnaires are rendered on SEEK’s candidate website and in our mobile apps.

By integrating questionnaires into your posting flow, you can enable hirers to:

- View and select question suggestions from SEEK’s library of commonly used and relevant questions.
- Add custom questions if they do not find the questions in SEEK’s existing library to be relevant.
- Select preferred response choices for each selected question.
- Attach a company privacy policy if applicable.

## Data structure

An [`ApplicationQuestionnaire`] is represented as an immutable collection of components.

- [`ApplicationPrivacyConsent`] allows the hirer to link to a privacy policy for candidates to consent to.

- [`ApplicationQuestion`] allows the hirer to ask a screening question:
  - `FreeText` presents the candidate with a free text input labelled with the question’s `questionHtml`.
    They must input at least one character before submitting their application.

  - `SingleSelect` presents the candidate with a set of mutually exclusive choices.
    They must select a single choice before submitting their application.

    SEEK renders these questions either as radio buttons or a dropdown list, depending on the number of choices.

  - `MultiSelect` presents the candidate with a set of non-exclusive choices.
    They must select at least one choice before submitting their application.

    SEEK renders these questions as a list of checkboxes.

`SingleSelect` and `MultiSelect` questions may be suggested from SEEK’s library via the [`applicationQuestionSuggestions`] query or created as custom by the hirer during the job posting flow.

### Questionnaire submission association

A candidate’s [questionnaire submission] can be retrieved through the [Optimised Apply] use case.
The submission can be correlated back to the presented questionnaire and its individual components.

### Preferred choices & scoring

The SEEK API can optionally calculate a numeric score for each questionnaire submission.
This requires that your software indicates which response choices are preferred by the hirer.
The calculated score and preference indicators are not otherwise used by SEEK.

Each `SingleSelect` and `MultiSelect` [`ApplicationQuestion`] specifies a collection of possible choices through the `responseChoice` field.
An [`ApplicationQuestionChoice`] can be preferenced by setting its `preferredIndicator` during questionnaire creation.
Candidate responses are scored against these preferred choices.

- The [`ApplicationQuestionnaireSubmission`] schema definition details scoring of the overall questionnaire submission
- The [`ApplicationQuestionResponse`] schema definition details scoring of individual questionnaire components

These scores are provided as an advisory screening indicator for the hirer.
All candidate applications will be delivered to your software;
SEEK does not support knockout questions and does not reject applications based on score.

### Limits

The SEEK API imposes the following limits on questionnaires:

- 56 [KiB] for the [create mutation] input
- 100 total components
- 1 `PrivacyConsent` component
- 99 response choices for `SingleSelect` or `MultiSelect` components.
- 1,000 [characters] for the [`ApplicationQuestionInput.questionHtml`] field
- 255 [characters] for every other text field, which is our [default limit]

Lengthy apply forms deter candidates from applying,
so we encourage hirers to keep their questionnaires short and focused.

### Immutability

For an equitable screening process,
a SEEK job ad presents the same questionnaire to all applicants.
This is enforced in two ways:

1. The [`ApplicationQuestionnaire`] object is immutable.

2. The [`updatePostedPositionProfile` mutation] will fail if a [`seekApplicationQuestionnaireId` field] is provided.

   To avoid confusion, your software’s frontend should not allow hirers to modify a live job ad’s questionnaire.

## Implementation options

The SEEK API supports multiple options for implementing questionnaires.

```mermaid Questionnaire options
flowchart TD
  start{Do you have a<br/> question library?} -->|Yes| option1(ATS Screening Questions)
  start -->|No| node2{Can you<br/> embed a SEEK-hosted<br/> panel?}
  node2 -->|Yes| option2("(SEEK-built) Questionnaire Panel")
  node2 -->|No| option3("Contact SEEK")
```

### Option 1: Questionnaire Panel

import { Alert, Text } from 'braid-design-system';

<Alert tone="promote">
  <Text>
    SEEK strongly recommends using the Questionnaire Panel for your integration.
    It will significantly reduce development time and ongoing maintenance, as
    well as provide an up-to-date user experience for SEEK hirers.
  </Text>
</Alert>

SEEK offers a Questionnaire Panel that you can embed into your posting flow.

Jump to the [panel section](/use-cases/job-posting/questionnaires/v2/panel) to get started.

### Option 2: Map existing questions in your ATS

If your software already has a mechanism for creating and managing questions, you can reuse them by internally mapping to the SEEK API’s questionnaire structure.

Jump to the [ATS Screening Questions section](/use-cases/job-posting/questionnaires/ats-questions) to get started.

[`ApplicationPrivacyConsent`]: /schema/named-type/ApplicationPrivacyConsent
[`ApplicationQuestion`]: /schema/named-type/ApplicationQuestion
[`ApplicationQuestionChoice`]: /schema/named-type/ApplicationQuestionChoice
[`ApplicationQuestionInput.questionHtml`]: /schema/named-type/ApplicationQuestionInput#/field/questionHtml
[`ApplicationQuestionnaire`]: /schema/named-type/ApplicationQuestionnaire
[`ApplicationQuestionnaireSubmission`]: /schema/named-type/ApplicationQuestionnaireSubmission
[`ApplicationQuestionResponse`]: /schema/named-type/ApplicationQuestionResponse
[`applicationQuestionSuggestions`]: /schema/query/applicationLibraryQuestionSuggestions
[`seekApplicationQuestionnaireId` field]: /schema/named-type/UpdatePostedPositionProfile_PositionProfileInput#/field/seekApplicationQuestionnaireId
[`updatePostedPositionProfile` mutation]: /use-cases/job-posting/managing-job-ads/updating-a-job-ad#updatepostedpositionprofile
[characters]: /graphql/seek-api-conventions#string-lengths
[create mutation]: /schema/mutation/createApplicationQuestionnaire
[default limit]: /graphql/seek-api-conventions#string-lengths
[KiB]: https://en.wikipedia.org/wiki/Kibibyte
[Optimised Apply]: /use-cases/application-export
[questionnaire submission]: /use-cases/application-export/questionnaire-submissions
